import {HideableValue, Values} from '@wix/devzai-utils-common';
import {WixMediaResource} from '@wix/devzai-common-wix';
import {DoppeHideableValue} from './doppe-hideable-value';
import {RichTextContent} from '@wix/devzai-utils-react';
import {DoppeDtoAction} from './doppe-dto-action';
import {BymoPageHasSearchProps} from '../bymo-pages';
import {
    BymoTemplateSpeedDialHeaderAppearance,
    BymoTemplateSpeedDialMenuAppearance,
    BymoTemplateSpeedDialSearchAppearance,
    BymoTemplateSpeedDialSocialLinksAppearance
} from '../../components/bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial-common';

// Define page types
export const DoppeDtoInnerPageHeaderLayout = {
    Default: 'Default',
    MediaBelow: 'MediaBelow'
} as const;

export type DoppeDtoInnerPageHeaderLayout = Values<typeof DoppeDtoInnerPageHeaderLayout>;

// Define page types
export const DoppeDtoInnerPageType = {
    Content: 'Content',
    Products: 'Products',
    Product: 'Product',
    BlogPosts: 'BlogPosts',
    Events: 'Events',
    Bookings: 'Bookings'
} as const;

export type DoppeDtoInnerPageType = Values<typeof DoppeDtoInnerPageType>;

export type DoppeDtoInnerPageVerticalListType =
    | typeof DoppeDtoInnerPageType.Products
    | typeof DoppeDtoInnerPageType.BlogPosts
    | typeof DoppeDtoInnerPageType.Events
    | typeof DoppeDtoInnerPageType.Bookings

export type DoppeDtoInnerPageVerticalItemType =
    | typeof DoppeDtoInnerPageType.Product


// export const DoppeDtoPageHomePageId = '38d849f2-6b73-48f8-8168-fccf60ac54d4';

export interface DoppeDtoInnerPageHeaderSettings extends BymoPageHasSearchProps {
    mainMedia: WixMediaResource[];
    title: DoppeHideableValue<string>;
    description: DoppeHideableValue<RichTextContent>;
    headerLayout: DoppeDtoInnerPageHeaderLayout;
    headerAppearance: BymoTemplateSpeedDialHeaderAppearance;
    // Main blocks appearance settings for inner pages (flat structure following template pattern)
    socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance;
    menuAppearance: BymoTemplateSpeedDialMenuAppearance;
}

export type DoppeDtoInnerPage =
    | DoppeDtoInnerPage.Content
    | DoppeDtoInnerPage.Products
    | DoppeDtoInnerPage.BlogPosts
    | DoppeDtoInnerPage.Events
    | DoppeDtoInnerPage.Bookings
    | DoppeDtoInnerPage.Product

export type DoppeDtoInnerVerticalListPage =
    | DoppeDtoInnerPage.Products
    | DoppeDtoInnerPage.BlogPosts
    | DoppeDtoInnerPage.Events
    | DoppeDtoInnerPage.Bookings;

export type DoppeDtoInnerVerticalItemPage =
    | DoppeDtoInnerPage.Product

export type DoppeDtoInnerPageWithHeaderSettings = DoppeDtoInnerVerticalListPage | DoppeDtoInnerPage.Content


export namespace DoppeDtoInnerPage {
    /**
     * Inner page ID type. Inner pages always have a string ID.
     * Note: The main/home page (which has null ID) is NOT an inner page.
     */
    export type Id = string;

    type BaseType<T extends DoppeDtoInnerPageType> = {
        id: string;
        name: string;
        slug: string;
        type: T;
    }

    export type Content = BaseType<typeof DoppeDtoInnerPageType.Content> &
        DoppeDtoInnerPageHeaderSettings & {
        actions: DoppeDtoAction[];
        // Search box content settings for inner pages
        searchBoxHeading: HideableValue<string>;
    };

    export type Products = BaseType<typeof DoppeDtoInnerPageType.Products> &
        DoppeDtoInnerPageHeaderSettings & {
        // For vertical pages - the Wix TPA App ID (null for content pages)
    };

    export type Product = BaseType<typeof DoppeDtoInnerPageType.Product> & {
        showAddToCardButton: boolean;
        showAdditionalSectionsInfo: boolean;
        showQuantitySelector: boolean;
        // For vertical pages - the Wix TPA App ID (null for content pages)
    };



    export type Bookings = BaseType<typeof DoppeDtoInnerPageType.Bookings> &
        DoppeDtoInnerPageHeaderSettings &
        {
            // For vertical pages - the Wix TPA App ID (null for content pages)
        };

    export type Events = BaseType<typeof DoppeDtoInnerPageType.Events> &
        DoppeDtoInnerPageHeaderSettings &
        {
            // For vertical pages - the Wix TPA App ID (null for content pages)
        };

    export type BlogPosts = BaseType<typeof DoppeDtoInnerPageType.BlogPosts> &
        DoppeDtoInnerPageHeaderSettings &
        {
            // For vertical pages - the Wix TPA App ID (null for content pages)
        };
}

/**
 * Derives the vertical page type from a WixTpaAppId
 */
export function doppeDtoInnerPageIsVerticalListType(pageType: DoppeDtoInnerPageType): boolean {
    switch (pageType) {
        case DoppeDtoInnerPageType.Products:
        case DoppeDtoInnerPageType.BlogPosts:
        case DoppeDtoInnerPageType.Events:
        case DoppeDtoInnerPageType.Bookings:
            return true;
        default:
            return false;
    }
}

/**
 * Derives the vertical page type from a WixTpaAppId
 */
export function doppeDtoInnerPageIsVerticalItemType(pageType: DoppeDtoInnerPageType): boolean {
    switch (pageType) {
        case DoppeDtoInnerPageType.Product:
            return true;
        default:
            return false;
    }
}

/**
 * Derives the vertical page type from a WixTpaAppId
 */
export function doppeDtoInnerPageIsContentType(pageType: DoppeDtoInnerPageType): boolean {
    switch (pageType) {
        case DoppeDtoInnerPageType.Content:
            return true;
        default:
            return false;
    }
}

/**
 * Type guard to check if an inner page is a Content page
 */
export function doppeDtoInnerPageIsContent(page: DoppeDtoInnerPage): page is DoppeDtoInnerPage.Content {
    return page.type === DoppeDtoInnerPageType.Content;
}

/**
 * Type guard to check if an inner page is a vertical page (Products, BlogPosts, Events, Bookings)
 */
export function doppeDtoInnerPageIsVerticalList(page: DoppeDtoInnerPage): page is DoppeDtoInnerVerticalListPage {
    return doppeDtoInnerPageIsVerticalListType(page.type);
}

/**
 * Type guard to check if an inner page is a vertical page (Products, BlogPosts, Events, Bookings)
 */
export function doppeDtoInnerPageIsVerticalItem(page: DoppeDtoInnerPage): page is DoppeDtoInnerVerticalItemPage {
    return doppeDtoInnerPageIsVerticalItemType(page.type);
}

export function doppeDtoInnerPageCreateDefaultProductsPage() {
    return {
        id: DoppeDtoInnerPageType.Products,
        name: 'Products',
        slug: 'products',
        type: DoppeDtoInnerPageType.Products,
        mainMedia: [],
        title: 'Products',
        description: '',
        headerLayout: DoppeDtoInnerPageHeaderLayout.Default,
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
        showLinksInSearch: false,
        showActionsInSearch: false,
        searchPlaceholder: ''
    } as DoppeDtoInnerPage.Products;
}

export function doppeDtoInnerPageCreateBlogPosts() {
    return {
        id: DoppeDtoInnerPageType.BlogPosts,
        name: 'Blog Posts',
        slug: 'blog-posts',
        type: DoppeDtoInnerPageType.BlogPosts,
        mainMedia: [],
        title: 'Blog Posts',
        description: '',
        headerLayout: DoppeDtoInnerPageHeaderLayout.Default,
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
        showLinksInSearch: false,
        showActionsInSearch: false,
        searchPlaceholder: ''
    } as DoppeDtoInnerPage.BlogPosts;
}

export function doppeDtoInnerPageCreateEvents() {
    return {
        id: DoppeDtoInnerPageType.Events,
        name: 'Events',
        slug: 'events',
        type: DoppeDtoInnerPageType.Events,
        mainMedia: [],
        title: 'Events',
        description: '',
        headerLayout: DoppeDtoInnerPageHeaderLayout.Default,
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
        showLinksInSearch: false,
        showActionsInSearch: false,
        searchPlaceholder: ''
    } as DoppeDtoInnerPage.Events;
}

export function doppeDtoInnerPageCreateBookings() {
    return {
        id: DoppeDtoInnerPageType.Bookings,
        name: 'Bookings',
        slug: 'bookings',
        type: DoppeDtoInnerPageType.Bookings,
        mainMedia: [],
        title: 'Bookings',
        description: '',
        headerLayout: DoppeDtoInnerPageHeaderLayout.Default,
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
        showLinksInSearch: false,
        showActionsInSearch: false,
        searchPlaceholder: ''
    } as DoppeDtoInnerPage.Bookings;
}

export function doppeDtoInnerPageCreateProductPageSettings() {
    return {
        id: DoppeDtoInnerPageType.Product,
        name: 'Product',
        slug: 'product',
        type: DoppeDtoInnerPageType.Product,
        mainMedia: [],
        title: 'Product',
        description: '',
        showAdditionalSectionsInfo: true,
        showAddToCardButton: false,
        showQuantitySelector: true,
    } as DoppeDtoInnerPage.Product;
}